
import { useParams } from 'react-router-dom';
import { useState, useEffect } from 'react';
import Navigation from '../../components/feature/Navigation';
import Footer from '../../components/feature/Footer';
import Button from '../../components/base/Button';
import SEOHead from '../../components/feature/SEOHead';

interface Project {
  id: number;
  title: string;
  description: string;
  image_url: string;
  video_url?: string;
  additional_images?: string[];
  additional_videos?: string[];
  client_name?: string;
  category?: string;
  project_year?: string;
  show_year?: number;
  show_category?: number;
}

interface GalleryItem {
  type: 'image' | 'video';
  url: string;
}

export default function ProjectPage() {
  const { id } = useParams();
  const projectId = id; // added to align with schema usage

  const [project, setProject] = useState<Project | null>(null);
  const [loading, setLoading] = useState(true);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentMediaIndex, setCurrentMediaIndex] = useState(0);
  const [gallery, setGallery] = useState<GalleryItem[]>([]);

  useEffect(() => {
    const fetchProject = async () => {
      try {
        // Use public API instead of authenticated API
        const response = await fetch(`/admin/public-api.php?action=get_project&id=${id}`);
        const data = await response.json();

        if (data.success && data.data) {
          const apiProject = data.data;
          
          // Parse additional images and videos
          let additionalImages: string[] = [];
          let additionalVideos: string[] = [];
          
          if (apiProject.additional_images) {
            try {
              // Try to parse as JSON first
              additionalImages = JSON.parse(apiProject.additional_images);
            } catch (e) {
              // If JSON parsing fails, treat as line-separated text
              additionalImages = apiProject.additional_images
                .split('\n')
                .map((url: string) => url.trim())
                .filter((url: string) => url.length > 0);
            }
          }
          
          if (apiProject.additional_videos) {
            try {
              // Try to parse as JSON first
              additionalVideos = JSON.parse(apiProject.additional_videos);
            } catch (e) {
              // If JSON parsing fails, treat as line-separated text
              additionalVideos = apiProject.additional_videos
                .split('\n')
                .map((url: string) => url.trim())
                .filter((url: string) => url.length > 0);
            }
          }

          const project: Project = {
            id: apiProject.id,
            title: apiProject.title,
            description: apiProject.description || '',
            image_url: apiProject.image_url,
            video_url: apiProject.video_url,
            additional_images: additionalImages,
            additional_videos: additionalVideos,
            client_name: apiProject.client_name,
            project_year: apiProject.project_year,
            show_year: apiProject.show_year,
            show_category: apiProject.show_category,
            category: apiProject.category,
          };

          setProject(project);

          // Build gallery from additional media
          const galleryItems: GalleryItem[] = [];
          
          // Add main video first if exists
          if (project.video_url) {
            galleryItems.push({
              type: 'video',
              url: project.video_url,
            });
          }

          // Add main image to gallery if it exists
          if (project.image_url) {
            galleryItems.push({
              type: 'image',
              url: project.image_url,
            });
          }

          // Add additional images (excluding main image to avoid duplication)
          if (project.additional_images && project.additional_images.length > 0) {
            project.additional_images.forEach((url) => {
              if (url && url.trim() && url.trim() !== project.image_url) {
                galleryItems.push({
                  type: 'image',
                  url: url.trim(),
                });
              }
            });
          }

          // Add additional videos (excluding main video to avoid duplication)
          if (project.additional_videos && project.additional_videos.length > 0) {
            project.additional_videos.forEach((url) => {
              if (url && url.trim() && url.trim() !== project.video_url) {
                galleryItems.push({
                  type: 'video',
                  url: url.trim(),
                });
              }
            });
          }

          setGallery(galleryItems);
        } else {
          console.log('Project not found or API failed:', data);
          setProject(null);
        }
      } catch (error) {
        console.error('Error fetching project:', error);
        setProject(null);
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchProject();
    }
  }, [id]);

  // Function to parse markdown-style formatting
  const parseDescription = (text: string) => {
    if (!text) return '';
    
    // Convert line breaks to <br> tags
    let formatted = text.replace(/\n/g, '<br>');
    
    // Convert **bold** to <strong>
    formatted = formatted.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
    
    // Convert *italic* to <em>
    formatted = formatted.replace(/\*(.*?)\*/g, '<em>$1</em>');
    
    return formatted;
  };

  // Function to check if URL is a Vimeo embed URL
  const isVimeoEmbed = (url: string) => {
    return url.includes('player.vimeo.com') || url.includes('vimeo.com');
  };

  // Function to convert Vimeo URL to embed format
  const getVimeoEmbedUrl = (url: string) => {
    if (url.includes('player.vimeo.com')) {
      // Already an embed URL, add autoplay parameters
      const separator = url.includes('?') ? '&' : '?';
      return `${url}${separator}autoplay=1&muted=1&loop=1`;
    }
    
    // Convert regular Vimeo URL to embed
    const vimeoId = url.match(/vimeo\.com\/(\d+)/)?.[1];
    if (vimeoId) {
      return `https://player.vimeo.com/video/${vimeoId}?autoplay=1&muted=1&loop=1`;
    }
    
    return url;
  };

  // Function to check if URL is a YouTube URL
  const isYouTubeUrl = (url: string) => {
    return url.includes('youtube.com') || url.includes('youtu.be');
  };

  // Function to convert YouTube URL to embed format
  const getYouTubeEmbedUrl = (url: string) => {
    let videoId = '';
    
    if (url.includes('youtube.com/watch?v=')) {
      videoId = url.split('v=')[1]?.split('&')[0];
    } else if (url.includes('youtu.be/')) {
      videoId = url.split('youtu.be/')[1]?.split('?')[0];
    }
    
    if (videoId) {
      return `https://www.youtube.com/embed/${videoId}?autoplay=1&mute=1&loop=1&playlist=${videoId}`;
    }
    
    return url;
  };

  // Lightbox functions
  const openLightbox = (index: number) => {
    setCurrentMediaIndex(index);
    setLightboxOpen(true);
    document.body.style.overflow = 'hidden';
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
    document.body.style.overflow = 'unset';
  };

  const nextMedia = () => {
    setCurrentMediaIndex((prev) => (prev + 1) % gallery.length);
  };

  const prevMedia = () => {
    setCurrentMediaIndex((prev) => (prev - 1 + gallery.length) % gallery.length);
  };

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if (!lightboxOpen) return;
      
      if (e.key === 'Escape') {
        closeLightbox();
      } else if (e.key === 'ArrowRight') {
        nextMedia();
      } else if (e.key === 'ArrowLeft') {
        prevMedia();
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [lightboxOpen, gallery.length]);

  const schemaData = {
    "@context": "https://schema.org",
    "@type": "CreativeWork",
    "name": project?.title || "Creative Project",
    "description": project?.description || "Creative project by Roel & Wietse",
    "url": `${import.meta.env.VITE_SITE_URL || "https://example.com"}/project/${projectId}`,
    "creator": {
      "@type": "Organization",
      "name": "Roel & Wietse Creative Team"
    },
    "image": project?.image_url,
    "dateCreated": (project as any)?.created_at,
    "genre": "Creative Design"
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-black border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-black mb-4">Project Not Found</h1>
          <Button onClick={() => window.history.back()}>Go Back</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <SEOHead
        title={project ? `${project.title} - Roel & Wietse Creative Team` : "Project - Roel & Wietse Creative Team"}
        description={project?.description || "Creative project showcasing our expertise in art direction and copywriting."}
        keywords="Creative project, Portfolio, Art direction, Copywriting, Design work"
        schemaData={schemaData}
      />
      
      <Navigation />

      {/* Hero Header */}
      <section className="relative h-screen overflow-hidden">
        {project.image_url ? (
          <img
            src={project.image_url}
            alt={project.title}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full bg-gray-900"></div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent"></div>

        {/* Hero Content */}
        <div className="absolute bottom-0 left-0 right-0 p-8 md:p-16">
          <div className="max-w-6xl mx-auto">
            <h1 className="text-6xl md:text-8xl font-black text-white uppercase tracking-widest mb-6 leading-tight">
              {project.title}
            </h1>

            {/* Project Meta */}
            <div className="flex flex-wrap items-center gap-6 text-white/80">
              {project.client_name && (
                <div className="flex items-center">
                  <i className="ri-user-line mr-2"></i>
                  <span>Client: {project.client_name}</span>
                </div>
              )}
              {project.category && project.show_category === 1 && (
                <div className="flex items-center">
                  <i className="ri-bookmark-line mr-2"></i>
                  <span>Category: {project.category}</span>
                </div>
              )}
              {project.project_year && project.show_year === 1 && (
                <div className="flex items-center">
                  <i className="ri-calendar-line mr-2"></i>
                  <span>Year: {project.project_year}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Project Description */}
      {project.description && (
        <section className="py-20 bg-white">
          <div className="max-w-4xl mx-auto px-8">
            <div className="prose prose-lg max-w-none text-center">
              <div 
                className="text-xl leading-relaxed text-gray-700"
                dangerouslySetInnerHTML={{ __html: parseDescription(project.description) }}
              />
            </div>
          </div>
        </section>
      )}

      {/* Gallery Section */}
      {gallery.length > 0 && (
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
              {gallery.map((item, index) => {
                // Check if this is the main video and should span 2 columns
                const isMainVideo = item.type === 'video' && item.url === project.video_url;
                const shouldSpanTwoColumns = isMainVideo;
                
                return (
                  <div 
                    key={index} 
                    className={`group cursor-pointer ${shouldSpanTwoColumns ? 'md:col-span-2' : ''}`}
                    onClick={() => openLightbox(index)}
                  >
                    {item.type === 'video' ? (
                      <div className={`aspect-video relative overflow-hidden rounded-lg shadow-xl hover:shadow-2xl transition-all duration-300 ${shouldSpanTwoColumns ? 'max-w-5xl mx-auto' : ''}`}>
                        {isVimeoEmbed(item.url) ? (
                          <iframe
                            src={getVimeoEmbedUrl(item.url)}
                            className="w-full h-full"
                            frameBorder="0"
                            allow="autoplay; fullscreen; picture-in-picture"
                            allowFullScreen
                            title={`Video ${index + 1}`}
                          />
                        ) : isYouTubeUrl(item.url) ? (
                          <iframe
                            src={getYouTubeEmbedUrl(item.url)}
                            className="w-full h-full"
                            frameBorder="0"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                            title={`Video ${index + 1}`}
                          />
                        ) : (
                          <video
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                            controls
                            muted
                          >
                            <source src={item.url} type="video/mp4" />
                          </video>
                        )}
                        {/* Expand Icon Overlay */}
                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all duration-300 flex items-center justify-center">
                          <div className="w-16 h-16 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                            <i className="ri-fullscreen-line text-black text-2xl"></i>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="aspect-[4/3] relative overflow-hidden rounded-lg shadow-xl hover:shadow-2xl transition-all duration-300">
                        <img
                          src={item.url}
                          alt={`Gallery image ${index + 1}`}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                        {/* Expand Icon Overlay */}
                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all duration-300 flex items-center justify-center">
                          <div className="w-16 h-16 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                            <i className="ri-fullscreen-line text-black text-2xl"></i>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Lightbox Modal */}
      {lightboxOpen && gallery.length > 0 && (
        <div className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center" style={{ zIndex: 99999 }}>
          {/* Close Button */}
          <button
            onClick={closeLightbox}
            className="absolute top-6 right-6 w-12 h-12 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-white/30 transition-all duration-300 cursor-pointer z-10"
          >
            <i className="ri-close-line text-white text-2xl"></i>
          </button>

          {/* Navigation Arrows */}
          {gallery.length > 1 && (
            <>
              <button
                onClick={prevMedia}
                className="absolute left-6 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-white/30 transition-all duration-300 cursor-pointer z-10"
              >
                <i className="ri-arrow-left-line text-white text-2xl"></i>
              </button>
              <button
                onClick={nextMedia}
                className="absolute right-6 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-white/30 transition-all duration-300 cursor-pointer z-10"
              >
                <i className="ri-arrow-right-line text-white text-2xl"></i>
              </button>
            </>
          )}

          {/* Media Counter */}
          {gallery.length > 1 && (
            <div className="absolute top-6 left-1/2 -translate-x-1/2 bg-white/20 backdrop-blur-sm rounded-full px-4 py-2 text-white text-sm font-medium">
              {currentMediaIndex + 1} / {gallery.length}
            </div>
          )}

          {/* Media Content */}
          <div className="relative w-full h-full max-w-7xl max-h-[90vh] p-6 flex items-center justify-center">
            {(() => {
              const currentItem = gallery[currentMediaIndex];
              if (!currentItem) return null;

              if (currentItem.type === 'video') {
                if (isVimeoEmbed(currentItem.url)) {
                  return (
                    <iframe
                      src={getVimeoEmbedUrl(currentItem.url)}
                      className="w-full h-full max-w-5xl max-h-full rounded-lg"
                      frameBorder="0"
                      allow="autoplay; fullscreen; picture-in-picture"
                      allowFullScreen
                    />
                  );
                } else if (isYouTubeUrl(currentItem.url)) {
                  return (
                    <iframe
                      src={getYouTubeEmbedUrl(currentItem.url)}
                      className="w-full h-full max-w-5xl max-h-full rounded-lg"
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    />
                  );
                } else {
                  return (
                    <video
                      className="w-full h-full max-w-5xl max-h-full object-contain rounded-lg"
                      controls
                      autoPlay
                      muted
                    >
                      <source src={currentItem.url} type="video/mp4" />
                    </video>
                  );
                }
              } else {
                return (
                  <img
                    src={currentItem.url}
                    alt={`Gallery image ${currentMediaIndex + 1}`}
                    className="w-full h-full max-w-full max-h-full object-contain rounded-lg"
                  />
                );
              }
            })()}
          </div>

          {/* Thumbnail Navigation */}
          {gallery.length > 1 && (
            <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex space-x-2 max-w-full overflow-x-auto px-4">
              {gallery.map((item, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentMediaIndex(index)}
                  className={`flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 transition-all duration-300 cursor-pointer ${
                    index === currentMediaIndex ? 'border-white' : 'border-white/30 hover:border-white/60'
                  }`}
                >
                  {item.type === 'image' ? (
                    <img
                      src={item.url}
                      alt={`Thumbnail ${index + 1}`}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full bg-gray-800 flex items-center justify-center">
                      <i className="ri-play-fill text-white text-lg"></i>
                    </div>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Navigation */}
      <section className="py-16 bg-black text-white">
        <div className="max-w-4xl mx-auto px-8 text-center">
          <div className="flex flex-col sm:flex-row gap-6 justify-center items-center">
            <Button
              variant="outline"
              size="lg"
              className="border-white text-white hover:bg-white hover:text-black rounded-lg whitespace-nowrap"
              onClick={() => window.history.back()}
            >
              ← Back to Work
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
